import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { API, useUser } from "../contexts/UserContext";
import Header from "../components/Header";
import { Progress } from "../components/ui/progress";
import {
 Cloud, Layers, Server, Network, Shield, Zap, Database, Lock, DollarSign, Award,
 Clock, CheckCircle2, ArrowRight, Bookmark, PlayCircle
} from "lucide-react";

const ICONS = {
 cloud: Cloud, layers: Layers, server: Server, network: Network,
 shield: Shield, zap: Zap, database: Database, lock: Lock,
 "dollar-sign": DollarSign, award: Award,
};

const Home = () => {
 const { user, progress, loading } = useUser();
 const navigate = useNavigate();
 const [course, setCourse] = useState(null);

 useEffect(() => {
 if (loading) return;
 if (!user) { navigate("/"); return; }
 axios.get(`${API}/course`).then((r) => setCourse(r.data));
 }, [user, navigate, loading]);

  const pct = progress?.percent_complete || 0;
  const completedModules = new Set(progress?.completed_modules || []);
  const bookmarks = new Set(progress?.bookmarks || []);
  const completedTopics = new Set(progress?.completed_topics || []);

  const hasStarted = (progress?.completed_topics?.length || 0) > 0;

  // Find the next-up topic: first incomplete topic in module order
  let resumeTarget = null;
  if (course && course.modules) {
    for (const m of course.modules) {
      for (const t of m.topics) {
        if (!completedTopics.has(t.id)) {
          resumeTarget = { moduleId: m.id, moduleOrder: m.order, moduleTitle: m.title, topicTitle: t.title };
          break;
        }
      }
      if (resumeTarget) break;
    }
  }

  useEffect(() => {
    // Only trigger completion if all topics specific to this course are completed
    if (course && course.modules.length > 0 && hasStarted && !resumeTarget) {
      window.parent.postMessage({ type: "COURSE_COMPLETED" }, "*");
    }
  }, [resumeTarget, hasStarted, course]);

  if (!user || !course) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center text-zinc-500">
        Loading...
      </div>
    );
  }

 return (
 <div className="min-h-screen bg-white ">
 <Header />
 <main className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
 {/* Greeting */}
 <div className="flex items-start md:items-center justify-between flex-col md:flex-row gap-6 mb-10">
 <div>
 <div className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-600 mb-2">
 Welcome back
 </div>
 <h1
 data-testid="home-greeting"
 className="font-heading text-3xl md:text-5xl font-black tracking-tighter text-zinc-900 "
 >
 Hello, {user.name.split(" ")[0]}.
 </h1>
 <p className="mt-2 text-base md:text-lg text-zinc-600 max-w-2xl">
 Your roadmap to understanding the cloud. Pick any module to begin or continue.
 </p>
 </div>

 </div>

 {/* Progress tracker */}
 <div
 data-testid="home-progress-tracker"
 className="mb-6 p-6 border border-zinc-200 rounded-sm bg-zinc-50 "
 >
 <div className="flex items-end justify-between mb-3">
 <div>
 <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">
 Course progress
 </div>
 <div className="font-heading text-3xl md:text-4xl font-black text-zinc-900 mt-1">
 {pct}%
 </div>
 </div>
 <div className="text-right text-sm text-zinc-600 ">
 <div>
 {progress?.completed_topics?.length || 0} / {progress?.total_topics || 34} topics
 </div>
 <div>
 {completedModules.size} / {course.modules.length} modules
 </div>
 </div>
 </div>
 <Progress value={pct} className="h-2 bg-zinc-200 [&>*]:bg-orange-500" />
 </div>

 {/* Resume where you left off */}
 {resumeTarget && (
 <Link
 to={`/module/${resumeTarget.moduleId}`}
 data-testid="resume-link"
 className="group mb-12 flex items-center justify-between gap-4 p-5 border border-orange-500 rounded-sm bg-orange-50 hover:bg-orange-100 :bg-orange-950/40 transition-colors"
 >
 <div className="flex items-center gap-4 min-w-0">
 <div className="shrink-0 w-11 h-11 rounded-sm bg-orange-500 text-black flex items-center justify-center">
 <PlayCircle className="w-5 h-5" />
 </div>
 <div className="min-w-0">
 <div className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-700 ">
 {hasStarted ? "Resume where you left off" : "Start your journey"}
 </div>
 <div className="font-heading text-lg font-bold text-zinc-900 truncate">
 Module {resumeTarget.moduleOrder} · {resumeTarget.topicTitle}
 </div>
 <div className="text-sm text-zinc-600 truncate">
 {resumeTarget.moduleTitle}
 </div>
 </div>
 </div>
 <ArrowRight className="shrink-0 w-5 h-5 text-orange-600 group-hover:translate-x-1 transition-transform" />
 </Link>
 )}

 {!resumeTarget && hasStarted && (
 <div
 data-testid="all-done-banner"
 className="mb-12 p-5 border-2 border-green-500 rounded-sm bg-green-50 flex items-center gap-4"
 >
 <CheckCircle2 className="w-8 h-8 text-green-600 shrink-0" />
 <div>
 <div className="font-heading text-lg font-bold text-zinc-900 ">
 Module Complete!
 </div>
 <div className="text-sm text-zinc-600 ">
 You may now return to the main LMS dashboard to view your official certificate.
 </div>
 </div>
 </div>
 )}

 {/* Modules grid */}
 <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
 {course.modules.map((m) => {
 const Icon = ICONS[m.icon] || Cloud;
 const done = completedModules.has(m.id);
 const moduleTopicIds = m.topics.map((t) => t.id);
 const topicsDone = moduleTopicIds.filter((tid) => (progress?.completed_topics || []).includes(tid)).length;
 const moduleBookmarked = m.topics.some((t) => bookmarks.has(t.id));
 return (
 <Link
 key={m.id}
 to={`/module/${m.id}`}
 data-testid={`module-card-${m.order}`}
 className="group relative p-6 border border-zinc-200 rounded-sm bg-white hover:border-orange-500 transition-all hover:-translate-y-0.5"
 >
 <div className="flex items-start justify-between mb-4">
 <div className={`w-11 h-11 rounded-sm flex items-center justify-center ${done ? "bg-orange-500 text-black" : "bg-zinc-100 text-zinc-700 group-hover:bg-orange-500 group-hover:text-black"} transition-colors`}>
 <Icon className="w-5 h-5" />
 </div>
 <div className="flex items-center gap-2">
 {moduleBookmarked && (
 <Bookmark className="w-4 h-4 text-orange-500 fill-orange-500" />
 )}
 {done && (
 <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-600 ">
 <CheckCircle2 className="w-4 h-4" />
 Done
 </span>
 )}
 </div>
 </div>
 <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-1">
 Module {m.order}
 </div>
 <h3 className="font-heading text-xl font-bold text-zinc-900 mb-2 leading-tight">
 {m.title}
 </h3>
 <p className="text-sm text-zinc-600 leading-relaxed mb-4 line-clamp-3">
 {m.summary}
 </p>
 <div className="flex items-center justify-between text-xs text-zinc-500 pt-4 border-t border-zinc-200 ">
 <div className="flex items-center gap-1.5">
 <Clock className="w-3.5 h-3.5" />
 {Math.round(m.duration_minutes / 60)}h · {m.topics.length} topics
 </div>
 <div className="flex items-center gap-1 font-semibold text-zinc-700 group-hover:text-orange-500 transition-colors">
 {topicsDone > 0 && !done ? `${topicsDone}/${m.topics.length} · ` : ""}
 {done ? "Review" : topicsDone ? "Continue" : "Start"}
 <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
 </div>
 </div>
 </Link>
 );
 })}
 </div>

 {/* Takeaways section */}
 <section className="mt-16">
 <h2 className="font-heading text-2xl md:text-3xl font-bold text-zinc-900 mb-2">
 What you&apos;ll walk away with
 </h2>
 <p className="text-sm md:text-base text-zinc-600 mb-6 max-w-2xl">
 One clear takeaway per module — real, lasting understanding you can put on your CV.
 </p>
 <div className="grid md:grid-cols-2 gap-4">
 {course.modules.map((m) => (
 <div
 key={m.id}
 className="p-5 border-l-2 border-orange-500 bg-zinc-50 "
 >
 <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-1">
 Module {m.order}
 </div>
 <div className="font-semibold text-zinc-900 mb-1">{m.title}</div>
 <div className="text-sm text-zinc-600 ">{m.takeaway}</div>
 </div>
 ))}
 </div>
 </section>
 </main>
 </div>
 );
};

export default Home;
